import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Drawer } from '@mui/material';
import { useLang } from '@spa/i18n/useLang';
import { CartContext } from '@extensions/providers/cartProvider';
import { selectIsSuperAdminNotMasquerading } from '@extensions/store/selectors';
import { useAppSelector } from '@/store';
import { SpaRoutes } from '@spa/constants/routes';
import type { CartLineItem } from '@spa/types/cart.types';
import BasicLoader from '@spa/components/BasicLoader';
import { useChannelNumberFormat } from '@spa/hooks/useChannelNumberFormat';

export function CartButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const t = useLang();
  const navigate = useNavigate();
  const { cart, removeItem, deleteCart } = useContext(CartContext);
  const { fmtInt } = useChannelNumberFormat();

  // @todo: legacy tech debt
  const isSuperAdminNotMasquerading = useAppSelector(
    selectIsSuperAdminNotMasquerading
  );

  if (isSuperAdminNotMasquerading) return null;

  const lineItems: CartLineItem[] = cart?.lineItems?.digitalItems ?? [];
  const itemCount = lineItems.length;
  const hasItems = itemCount > 0;

  const totalWeight = lineItems.reduce(
    (acc, item) => acc + (item.quantity ?? 0),
    0
  );

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const onConfirm = () => {
    onClose();
    navigate(SpaRoutes.CHECKOUT);
  };

  const onClear = async () => {
    setIsClearing(true);
    document.body.style.overflow = 'hidden';
    await deleteCart(cart?.entityId);
    document.body.style.overflow = '';
    setIsClearing(false);
    onClose();
  };

  const onRemoveItem = async (entityId: string) => {
    setRemovingItemId(entityId);
    await removeItem(entityId);
    setRemovingItemId(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('global.cart.title')}
        className="
          flex items-center gap-2
          bg-primary-text!
          hover:bg-biomar-900!
          transition-colors
          duration-200
          text-white px-6 py-3 rounded-md text-sm
        "
      >
        <div className="relative">
          <ShoppingCartOutlinedIcon sx={{ fontSize: '24px' }} />
          {hasItems && (
            <span
              className="
                absolute -top-2.5 -right-2.5
                bg-ocean-blue text-white
                text-[10px] font-bold
                w-5 h-5 rounded-full
                flex items-center justify-center
              "
            >
              {itemCount}
            </span>
          )}
        </div>
        {t('global.cart.title')}
      </button>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '450px',
            '@media (max-width: 768px)': {
              maxWidth: '90%',
            },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Clearing overlay */}
        {isClearing && (
          <div
            className="fixed top-0 right-0 z-200 flex items-center justify-center bg-white/70"
            style={{ width: '450px', height: '100vh' }}
          >
            <BasicLoader />
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between px-6! py-5! border-b border-biomar-border">
          <div className="flex items-center gap-3">
            <ShoppingCartOutlinedIcon
              sx={{ fontSize: '24px', color: 'var(--color-primary-text)' }}
            />
            <h2 className="text-base! text-primary-text! m-0!">
              {t('global.cart.title')}
            </h2>
            <span className="text-muted-foreground! text-xs">
              {hasItems
                ? `(${itemCount} ${
                    itemCount === 1
                      ? t('global.cart.item')
                      : t('global.cart.items')
                  })`
                : `(0 ${t('global.cart.items')})`}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-primary-text! transition-colors"
          >
            <CloseOutlinedIcon sx={{ fontSize: '24px' }} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto relative flex flex-col">
          {removingItemId && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
              <BasicLoader />
            </div>
          )}

          {!hasItems ? (
            <p className="text-center! text-muted-foreground! font-semibold m-auto! text-sm">
              {t('global.cart.empty')}
            </p>
          ) : (
            <ul className="m-0! list-none p-0!">
              {lineItems.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between px-5! py-4! border-b border-biomar-border"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-12 h-12 object-contain"
                    />
                    <div>
                      <p className="font-semibold! text-primary-text m-0! text-sm!">
                        {item.name}
                      </p>
                      <p className="text-muted-foreground text-xs m-0!">
                        {item.sku} · {fmtInt(item.quantity)}{' '}
                        {t('global.cart.unitKg')}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    aria-label={t('global.cart.removeItem')}
                    disabled={removingItemId === item.entityId}
                    className="text-muted-foreground hover:text-amber! 
                      transition-colors disabled:opacity-50! disabled:cursor-not-allowed!
                    "
                    onClick={() => onRemoveItem(item.entityId)}
                  >
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: '24px' }} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {hasItems && (
          <div>
            <div className="flex items-center justify-between px-6! py-3! border-y border-biomar-border! text-muted-foreground text-sm">
              <span>{t('global.cart.total')}</span>
              <span className="text-primary-text! font-semibold">
                {fmtInt(totalWeight)} {t('global.cart.unitKg')}
              </span>
            </div>
            <div className="flex gap-3 px-6! pb-6! pt-6!">
              <button
                type="button"
                onClick={onClear}
                disabled={isClearing}
                className="
                  flex-1 py-2.5! rounded-md
                  border! border-biomar-border!
                text-primary-text! font-normal!
                  text-sm! hover:bg-gray-50! transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed!
                "
              >
                {t('global.cart.clear')}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="
                  flex-2 py-2.5! rounded-md
                  bg-primary-text!
                  hover:bg-biomar-900!
                  text-white! text-sm! font-semibold!
                  transition-colors duration-200
                "
              >
                {t('global.cart.confirm')}
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
}

export default CartButton;
